/* eslint-disable @typescript-eslint/no-explicit-any -- generic table component used with varying row data types across consumers */
import * as React from 'react';
import {useEffect, useState} from 'react';
import classnames from 'classnames';
import {ExpandedState, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingFn, SortingState, useReactTable,} from '@tanstack/react-table';

import {LOCALE} from 'features/ui/enums';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import { CaseStatus } from 'features/errands/enums';
import { Errand } from 'features/errands/interfaces';
import {isNumber} from 'features/services/utils/helpers';
import {getCurrentLocale} from 'features/services/utils/env-helper';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import { getErrandAddress } from 'features/errands/helpers';
import {DisposalForm, ObjectStatus, ParkingStatus, ResidenceType, SortBy} from 'features/search/filters/enums';

import './style.scss';

import {Icon} from '@atoms/Media/Icon';
import {TableRow} from './_tableRow';
import {SortController} from "@organisms/ResidenceSearch/SearchResult/SortController";
import {Button, ButtonColor, ButtonSize, ButtonType} from '@atoms/LinksAndButtons/Button';
import {DataTableCardType, TableCards} from './CardView';
import { ColumnDef, Row, VisibilityState } from '@tanstack/react-table';

interface Props {
    id: string;
    data: any[];
    columns: ColumnDef<any, any>[];
    columnVisibility: VisibilityState;
    currentItem: any;
    currentObjectId: any;
    onRowClick?: ((row: any) => void) | null;
    foldableCount: number;
    cardType?: DataTableCardType | null;
    defaultSort: string;
    defaultDesc?: boolean;
    notSortable?: boolean;
    notExpandable?: boolean;
    editing?: boolean;
    noHover?: boolean;
    onDeleteRow?: (row: any) => void;
    onOrderDown?: (row: any) => void;
    onOrderUp?: (row: any) => void;
    renderExpandedContent?: (row: any) => React.ReactNode;
    getRowCanExpand?: (row: any) => boolean;
    onSortingChange?: (sorting: SortingState) => void;
    /** Extra knappar i verktygsraden, renderas till vänster om vy-toggeln. */
    toolbarActions?: React.ReactNode;
}

export function Table({
    id,
    data, columns, columnVisibility,
    currentItem, currentObjectId, onRowClick,
    foldableCount, cardType, defaultSort, defaultDesc = false, notSortable = false, notExpandable = false,
    editing = false,  noHover = false, onDeleteRow = () => {}, onOrderDown = () => {}, onOrderUp = () => {},
    renderExpandedContent,
    getRowCanExpand = () => false,
    onSortingChange,
    toolbarActions,
}: Props) {
    const locale = getCurrentLocale();
    const isFinnish = locale === LOCALE.FI;
    const isNorwegian = locale === LOCALE.NO;
    const isMobile = useDeviceStore(selectIsMobile);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const [cardSort, setCardSort] = useState(SortBy.ACCESS_ASC);
    const [sorting, setSorting] = React.useState<SortingState>([{
        id: defaultSort,
        desc: defaultDesc,
    }]);

    const sort = (obj, value, key) => {
        const desc = !!table.getState().sorting.find(x => x.id === key)?.desc;
        const tempValue = 1;

        if(obj.objectStatus === ObjectStatus.SOLD || obj.objectStatus === ObjectStatus.BOOKED) {
            value = isNumber(value)
                ? desc
                    ? value / 100
                    : value * 100
                : desc
                    ? tempValue / 100
                    : tempValue * 100
        }

        return value;
    };
    const sortWithObjectStatus = (a, b, key) => {
        const fixedKey = key.replace('pricelist-', '');

        const aSort = sort(a.original, a.original[fixedKey], key);
        const bSort = sort(b.original, b.original[fixedKey], key);

        return aSort === bSort ? 0 : aSort > bSort ? 1 : -1;
    }

    const getTotalPrice = (obj) => {
        let price = obj.price ? obj.price : 0;

        if(obj.otherExpenses) {
            price += obj.otherExpenses;
        }
        if(obj.jmnoFellesgjeld
            && obj.residenceType === ResidenceType.APARTMENT
            && (obj.disposalForm === DisposalForm.OWNERSHIP || obj.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)
        ) {
            price += obj.jmnoFellesgjeld;
        }

        return price;
    }
    const sortTotalPrice = (a, b, key) => {
        const aValue = sort(a.original, getTotalPrice(a.original), key);
        const bValue = sort(b.original, getTotalPrice(b.original), key);

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }

    const sortPrimaryArea = (a, b, key) => {
        let aValue = (getCurrentLocale() === LOCALE.NO && a.original.jmnoBraInternal)
            ? a.original.jmnoBraInternal
            : a.original.primaryArea;
        let bValue = (getCurrentLocale() === LOCALE.NO && b.original.jmnoBraInternal)
            ? b.original.jmnoBraInternal
            : b.original.primaryArea;

        //console.log('---');
        //console.log(a.original.apartmentNumber, a.original.jmnoBraInternal, a.original.primaryArea, aValue);
        //console.log(b.original.apartmentNumber, b.original.jmnoBraInternal, b.original.primaryArea, bValue);

        aValue = sort(a.original, aValue, key);
        bValue = sort(b.original, bValue, key);

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
    const sortRooms = (a, b, key) => {
        let aValue = a.original.possibleRooms ? (a.original.rooms + a.original.possibleRooms)/2 : a.original.rooms;
        let bValue = b.original.possibleRooms ? (b.original.rooms + b.original.possibleRooms)/2 : b.original.rooms;
        aValue = sort(a.original, aValue, key);
        bValue = sort(b.original, bValue, key);

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
    const sortFloor = (a, b, key) => {
        let aValue = a.original.floor
            ? a.original.floor
            : a.original.maxFloor
                ? 0
                : -1;
        let bValue = b.original.floor
            ? b.original.floor
            : b.original.maxFloor
                ? 0
                : -1;

        aValue = sort(a.original, aValue, key);
        bValue = sort(b.original, bValue, key);

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
    const sortDate = (a, b, key) => {
        let aValue = a.original.inhabitDate;
        let bValue = b.original.inhabitDate;
        aValue = sort(a.original, aValue, key);
        bValue = sort(b.original, bValue, key);

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
    const sortParking = (a, b, key) => {
        const statusA = a.original.parkingSpaceStatus;
        const statusB = b.original.parkingSpaceStatus;
        const desc = !!table.getState().sorting.find(x => x.id === key)?.desc;
        const statusOrder = [ParkingStatus.INCLUDED, ParkingStatus.TWO_INCLUDED, ParkingStatus.CAN_PURCHASE, ParkingStatus.CAN_PURCHASE2, ParkingStatus.CANNOT_PURCHASE, ParkingStatus.UNKNOWN];

        let aValue = statusOrder.indexOf(statusA);
        let bValue = statusOrder.indexOf(statusB);

        if(a.original.objectStatus === ObjectStatus.SOLD || a.original.objectStatus === ObjectStatus.BOOKED) {
            aValue = desc ? (aValue - 100) : (aValue + 100);
        }
        if(b.original.objectStatus === ObjectStatus.SOLD || b.original.objectStatus === ObjectStatus.BOOKED) {
            bValue = desc ? (bValue - 100) : (bValue + 100);
        }

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
    const sorCoOwning = (a, b, key) => {
        let aValue = sort(a.original, a.original.possibleCoOwning, key);
        let bValue = sort(b.original, b.original.possibleCoOwning , key);
        aValue += (a.original.possibleCoOwning) ? -1 : 1;
        bValue += (b.original.possibleCoOwning) ? -1 : 1;

        return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
    }
    const sortErrandStatus: SortingFn<Errand> = (rowA, rowB) => {
        const statusA = rowA.original.caseStatusId as CaseStatus;
        const statusB = rowB.original.caseStatusId as CaseStatus;
        const statusOrder = [
            CaseStatus.REGISTERED, CaseStatus.REGISTERED_NO, CaseStatus.REGISTERED_FI,
            CaseStatus.ONGOING, CaseStatus.ONGOING_NO, CaseStatus.ONGOING_FI, CaseStatus.EXECUTED_NO, CaseStatus.NOT_RESPONDING, CaseStatus.WAITING_ON_CUSTOMER, CaseStatus.WAITING_ON_DECISION, CaseStatus.WAITING_ON_SUBCONTRACTOR, CaseStatus.WAITING_ON_WEATHER, CaseStatus.WAITING_ON_MATERIAL, CaseStatus.WAITING_ON_INVESTIGATION, CaseStatus.CUSTOMER_BOOKED, CaseStatus.CUSTOMER_NOT_BOOKED, CaseStatus.MATERIALS, CaseStatus.SUBCONTRACTOR, CaseStatus.INVESTIGATION, CaseStatus.SUBCONTRACTOR_NO_LOGIN, CaseStatus.SUBCONTRACTOR_NO_LOGIN_NO, CaseStatus.INFO_FROM_CUSTOMER, CaseStatus.BOOKED, CaseStatus.BOOKED_FI, CaseStatus.OTHER_SEASON,
            CaseStatus.NOT_RESPONDING_FI,
            CaseStatus.WAITING_ON_INVESTIGATION_FI,
            CaseStatus.WAITING_ON_SUBCONTRACTOR_FI,
            CaseStatus.WAITING_ON_MATERIAL_FI,
            CaseStatus.WAITING_ON_WEATHER_FI,
            CaseStatus.WAITING_ON_DECISION_FI,
            CaseStatus.WAITING_ON_SUBCONTRACTOR_NO_PORTAL,
            CaseStatus.WAITING_ON_CUSTOMER_FI,

            CaseStatus.EXECUTED, CaseStatus.EXECUTED_FI,
            CaseStatus.REFERENCE, CaseStatus.REFERENCE_NO, CaseStatus.COMPLETED, CaseStatus.COMPLETED_NO, CaseStatus.COMPLETED_FI,  CaseStatus.SHREDDED, CaseStatus.SHREDDED_NO, CaseStatus.SHREDDED_FI, CaseStatus.CANCELLED_BY_CUSTOMER, CaseStatus.CANCELLED_BY_CUSTOMER_NO, CaseStatus.CANCELLED_BY_CUSTOMER_FI
        ];
        const sortValue = statusOrder.indexOf(statusA) - statusOrder.indexOf(statusB);

        return sortValue;
    }
    const sortErrandAddress = (a, b) => {
        const aValue = getErrandAddress(a.original);
        const bValue = getErrandAddress(b.original);

        if (aValue < bValue) {
            return -1;
        }
        if (aValue > bValue) {
            return 1;
        }

        return 0;
    }

    const [expanded, setExpanded] = useState<ExpandedState>({})
    const expandClick = (row: Row<any>) => {
        row.getToggleExpandedHandler()();
    }

    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getRowCanExpand: (row) => getRowCanExpand(row.original),
        getExpandedRowModel: getExpandedRowModel(),
        onExpandedChange: setExpanded,
        getSortedRowModel: getSortedRowModel(), //client-side sorting
        onSortingChange: (updater) => {
            setSorting(updater);
            const next = typeof updater === 'function' ? updater(sorting) : updater;
            onSortingChange?.(next);
        },
        enableSortingRemoval: false, // disable the ability to remove sorting on columns (always none -> asc -> desc -> asc)
        enableSorting: !notSortable,
        state: {
            columnVisibility,
            sorting,
            expanded: expanded,
        },
        sortingFns: {
            sortWithObjectStatus: sortWithObjectStatus,
            sortTotalPrice: sortTotalPrice,
            sortPrimaryArea: sortPrimaryArea,
            sortRooms: sortRooms,
            sortFloor: sortFloor,
            sortDate: sortDate,
            sortParking: sortParking,
            sorCoOwning: sorCoOwning,
            sortErrandStatus: sortErrandStatus,
            sortErrandAddress: sortErrandAddress
        }
    });

    const [folded, setFolded] = useState<boolean>(true);
    const toggleFold = () => {
        setFolded(!folded);
    };
    const [tableView, setTableView] = useState<boolean>(!isMobile || cardType === null || cardType === undefined);
    const toggleView = () => {
        setTableView(!tableView);
    };

    const getTemplateStyleString = () => {
        let string = '';
        table.getHeaderGroups().map(headerGroup => {
            headerGroup.headers.map(column => {
                /*if((isFinnish && column.id === 'errands-ticketNumber')
                ) {
                    string += ` 180px`
                } else*/ if((isFinnish && column.id === 'errands-objectAddress')
                    || (isFinnish && column.id === 'errands-ticketNumber')
                    || (isFinnish && column.id === 'errands-caseTypeName')
                ) {
                    string += ` minmax(160px, 2.5fr)`
                } else if((isFinnish && column.id === 'errands-creationDate')
                    || (isFinnish && column.id === 'errands-caseStatusId')
                    || (isFinnish && column.id === 'errands-room')
                    || (isFinnish && column.id === 'errands-assetType')
                    || (column.id === 'pricelist-status') //110
                    || (column.id === 'rounds-nrOfCaseRemarks') // 130px
                    || (column.id === 'rounds-closeDate') // 140px
                ) {
                    string += ` 195px`
                }

                else if((column.id === 'pricelist-price')
                    || (column.id === 'pricelist-communityFee')
                    || (column.id === 'pricelist-stageName')
                    || (column.id === 'document-agreementMyPagesStatus')
                    || (column.id === 'parking-price')
                ) {
                    string += ` minmax(130px, 230px)`
                }

                else if((column.id === 'pricelist-address') //180
                    || (column.id === 'premises-inhabitDate')
                    || (column.id === 'purchase-inhabit-date')
                    || (column.id === 'document-type')
                ) {
                    string += ` minmax(210px, 330px)`
                }

                else if((column.id === 'pricelist-totalPrice' && isNorwegian) //180
                    || (column.id === 'pricelist-fee') //180
                    || (column.id === 'pricelist-primaryArea') //180
                    || (column.id === 'pricelist-floor') //180
                    || (column.id === 'pricelist-jmnoSharedCost')
                    || (column.id === 'pricelist-parkingspaceStatus') //180
                    || (column.id === 'purchase-order')
                    || (column.id === 'purchase-fee')
                    || (column.id === 'errands-ticketNumber') // 160px
                    || (column.id === 'errands-caseTypeName') // 70-140px
                ) {
                    string += ` minmax(120px, 210px)`
                }

                else if((column.id === 'pricelist-totalPrice' && !isNorwegian)
                    || (column.id === 'pricelist-jmoyTotalPrice')
                    || (column.id === 'pricelist-secondaryArea')
                    || (column.id === 'pricelist-inhabitDate')
                    || (column.id === 'jobs-title')
                    || (column.id === 'purchase-price')
                    || (column.id === 'errands-caseStatusId') // 130px
                    || (column.id === 'errands-room')
                    || (column.id === 'errands-assetType')
                    || (column.id === 'errands-objectAddress')
                    || (column.id === 'document-name')
                    || (column.id === 'rounds-patrollingTypeName')
                    || (column.id === 'parking-jmoyTotalPrice')
                    || (column.id === 'parking-objectNumber')
                    || (column.id === 'parking-shareType')
                    || (column.id === 'parking-fee')
                ) {
                    string += ` minmax(180px, 2.5fr)`
                }

                else if((column.id === 'pricelist-rooms' && isFinnish)
                    || (column.id === 'purchase-rooms')
                    || (column.id === 'row-edit')
                    || (column.id === 'errands-creationDate') // 100px
                    || (column.id === 'errands-expand') // 100px
                    || (column.id === 'rounds-expand') // 80px
                    || (column.id === 'parking-status')
                ) {
                    string += ` 150px`
                }

                else if((column.id === 'pricelist-id') //60
                    || (column.id === 'pricelist-rooms' && !isFinnish)
                ) {
                    string += ` minmax(80px, 100px)`
                }

                else if ((column.id === 'row-url')) {
                    //console.log('!');
                    string += ' 0'
                }

                else {
                    string += ' minmax(130px, 1fr)';
                }
            })
        });

        return string;
    };
    const hasExpandableRows = data?.some(row => getRowCanExpand(row)) ?? false;
    const baseColumnTemplate = getTemplateStyleString();
    const columnTemplate = hasExpandableRows
        ? `48px ${baseColumnTemplate}`
        : baseColumnTemplate;

    const [hasScroll, setHasScroll] = useState(false);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;

        const checkScroll = () => {
        const scrollExists = el.scrollHeight > el.clientHeight;
        setHasScroll(scrollExists);
        };

        checkScroll();

        // Om tabellen kan ändra höjd dynamiskt:
        const observer = new ResizeObserver(checkScroll);
        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    const wrapperClasses = classnames('table__wrapper', {
        'table__wrapper--expanded': !folded,
        'table__wrapper--scroll': false,
        'table__wrapper--hover': (!editing && !noHover),
    });

    const boxClasses = classnames('table__box', {
        'table__box--scroll': hasScroll,
    });

    const apartmentIsVisible = (index, apartmentNumber) => {
        if(!folded || index < foldableCount) return true;
        if(apartmentNumber === currentItem || apartmentNumber === currentObjectId) return true;
        return false;
    }
    const rowIsVisible = (index) => {
        if(!folded || index < foldableCount) return true;
        return false;
    }

    return (
        <div className={wrapperClasses}>
            {cardType && <div className='table__top'>
                <div>
                    {!tableView && cardType !== DataTableCardType.ERRANDS &&
                        <SortController
                            currentSorting={cardSort}
                            onSortChange={(currentSort ) => {
                                setCardSort(currentSort);
                            }}
                            showRecommendedSort={false}
                            hideGeoLocationButton
                        />
                    }

                </div>
                <div className='table__top-actions'>
                    {toolbarActions}
                    <Button
                        onClick={toggleView}
                        iconFirst
                        icon={tableView
                            ? <Icon name="list" />
                            : <Icon name="table" />
                        }
                        buttonColor={ButtonColor.DEFAULT}
                        buttonSize={ButtonSize.SMALL}
                        buttonType={ButtonType.GHOST}
                    >
                        {tableView
                            ? getFormattedMessage('jm.components.DataTable.listView')
                            : getFormattedMessage('jm.components.DataTable.tableView')
                        }
                    </Button>
                </div>
            </div>}
            {table.getRowModel().rows.length > foldableCount &&
                <div className='visually-hidden'>
                    <Button
                        onClick={toggleFold}
                        ariaExpanded={!folded}
                        ariaControls={id}
                        buttonColor={ButtonColor.DEFAULT}
                        buttonSize={ButtonSize.DEFAULT}
                        buttonType={ButtonType.GHOST}
                    >
                        {!folded
                            ? getFormattedMessage('jm.components.Table.showLess')
                            : getFormattedMessage('jm.components.Table.showMore')
                        }
                    </Button>
                </div>
            }
            {tableView
                ? <div
                    className={boxClasses}
                >
                    <div
                        className='table__scrollbox'
                        ref={tableContainerRef}
                    >
                        <table className="table" id={id}>
                            <thead className='table__header'>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr
                                        key={headerGroup.id}
                                        style={{
                                            gridTemplateColumns: columnTemplate,
                                        }}
                                    >
                                        {headerGroup.headers.map((header, headerIndex) => (
                                            <th
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                                scope="col"
                                                className={header.column.id === 'row-url' ? 'no-padd' : undefined}
                                                style={hasExpandableRows && headerIndex === 0 ? { gridColumn: 'span 2', textAlign: 'left' } : undefined}
                                            >
                                                <span className='cell-content'>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )
                                                    }
                                                    {!notSortable && <>
                                                        {{
                                                            asc: <Icon name="chevron" />,
                                                            desc: <Icon name="chevron" flip />,
                                                        }[header.column.getIsSorted() as string] ?? <span className='dummy-icon icon' aria-hidden="true" />}
                                                    </>}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="table__body">
                                {table.getRowModel().rows.map((row, index) => {
                                    return (
                                        <React.Fragment key={row.id}>
                                            <TableRow
                                                key={row.id}
                                                row={row}
                                                rowIsVisible={(cardType===DataTableCardType.PRICELIST) ? apartmentIsVisible(index, row.getValue('pricelist-apartmentNumber')) : rowIsVisible(index)}
                                                columnTemplate={columnTemplate}
                                                currentItem={currentItem}
                                                currentObjectId={currentObjectId}
                                                onRowClick={getRowCanExpand(row.original) ? () => expandClick(row) : onRowClick}
                                                pricelist={cardType===DataTableCardType.PRICELIST}
                                                isExpandable={getRowCanExpand(row.original)}
                                                isExpanded={row.getIsExpanded()}
                                            />
                                            {row.getIsExpanded() && (
                                                <tr>
                                                    <td colSpan={row.getAllCells().length}> 
                                                        {renderExpandedContent?.(row.original)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                : <TableCards
                    foldableCount={foldableCount}
                    isFolded={folded}
                    cardType={cardType ?? undefined}
                    data={table.getRowModel().rows.map(row => row.original)}
                    editing={editing}
                    onDeleteRow={onDeleteRow}
                    sort={cardSort}
                    //emptyMessage={emptyMessage}
                    onEditDown={onOrderDown}
                    onEditUp={onOrderUp}
                    onRowClick={onRowClick as ((data: any) => (e: any) => any) | undefined}
                />
            }
            {table.getRowModel().rows.length > foldableCount && !notExpandable &&
                <div className='table__fold-button'>
                    <Button
                        onClick={toggleFold}
                        ariaExpanded={!folded}
                        ariaControls={id}
                        buttonColor={ButtonColor.DEFAULT}
                        buttonSize={ButtonSize.DEFAULT}
                        buttonType={ButtonType.GHOST}
                    >
                        {folded
                            ? cardType === DataTableCardType.ERRANDS
                                ? getFormattedMessage('jm.components.Table.showMoreErrands')
                                : getFormattedMessage('jm.components.Table.showMore')
                            : cardType === DataTableCardType.ERRANDS
                                ? getFormattedMessage('jm.components.Table.showLessErrands')
                                : getFormattedMessage('jm.components.Table.showLess')
                        }
                    </Button>
                </div>
            }
        </div>
    )
}
